import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Calculator, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency } from "@/lib/api";

// Experian Q2 2026 — média de APR de usados por faixa de crédito
const TIERS = [
  { id: "otimo", label: "Crédito Ótimo", score: "781+", apr: 6.3 },
  { id: "bom", label: "Crédito Bom", score: "661-780", apr: 8.8 },
  { id: "regular", label: "Regular", score: "601-660", apr: 13.9 },
  { id: "ruim", label: "Crédito Ruim", score: "até 600", apr: 19.1 },
];

export const FinanceSimModal = ({ vehicle, onClose }) => {
  const [down, setDown] = useState("");
  const [apr, setApr] = useState(8.8);
  const [tier, setTier] = useState("bom");
  const [docFee, setDocFee] = useState(vehicle?.doc_fee_amount || 499);
  const [regFee, setRegFee] = useState(vehicle?.registration_cost || 150);
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);

  if (!vehicle) return null;
  const price = Number(vehicle.sale_price || 0);
  const financed = Math.max(0, price - Number(down || 0) + price * 0.0625 + Number(docFee || 0) + Number(regFee || 0));
  const finCalc = financed > 0 ? financed + 2000 : 0;
  const pmt = (n) => {
    const r = Number(apr) / 100 / 12;
    if (!financed || !n) return 0;
    if (r <= 0) return finCalc / n;
    return (finCalc * r) / (1 - Math.pow(1 + r, -n));
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/finance-sim-pdf`, {
        down: Number(down || 0), apr: Number(apr || 0),
        doc_fee: Number(docFee || 0), registration_fee: Number(regFee || 0), lang,
      }, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Não consegui gerar a simulação — tente novamente");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="finance-sim-modal" className="bg-surface border border-border w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <p className="label-eyebrow text-primary flex items-center gap-2"><Calculator size={13} /> Simulação de Financiamento</p>
          <button type="button" onClick={onClose} data-testid="finance-sim-close" className="text-text-secondary hover:text-white transition-colors"><X size={16} /></button>
        </div>
        <p className="font-display font-bold text-sm mb-3">{vehicle.year} {vehicle.make} {vehicle.model} · {formatCurrency(price)}</p>

        <label className="label-eyebrow block mb-1.5">Faixa de crédito do cliente</label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {TIERS.map((tr) => (
            <button key={tr.id} type="button" data-testid={`sim-tier-${tr.id}`}
              onClick={() => { setTier(tr.id); setApr(tr.apr); }}
              className={`border px-3 py-2 text-left transition-colors ${tier === tr.id ? "border-[#C9A227] bg-[#C9A227]/10" : "border-border hover:border-[#C9A227]/60"}`}>
              <span className="block font-display font-bold uppercase text-[11px] tracking-wide">{tr.label}</span>
              <span className="block text-[10px] text-text-secondary">score {tr.score} · ~{tr.apr}% APR</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Entrada (US$)</label>
            <input data-testid="sim-down" type="number" value={down} onChange={(e) => setDown(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm" placeholder="0" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Juros APR (%)</label>
            <input data-testid="sim-apr" type="number" step="0.1" value={apr} onChange={(e) => setApr(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Doc fee (US$)</label>
            <input data-testid="sim-doc" type="number" value={docFee} onChange={(e) => setDocFee(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Registro e título (US$)</label>
            <input data-testid="sim-reg" type="number" value={regFee} onChange={(e) => setRegFee(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm" />
          </div>
        </div>

        {/* Prévia rápida */}
        <div className="border border-border bg-background p-3 mb-3">
          <p className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">Valor financiado (com imposto MA 6.25%)</p>
          <p className="font-display font-black text-lg text-[#C9A227]" data-testid="sim-financed">{formatCurrency(financed)}</p>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {[36, 48, 60, 72].map((n) => (
              <div key={n} className="text-center border border-border py-1.5">
                <p className="text-[9px] text-text-secondary">{n} meses</p>
                <p className="font-display font-bold text-sm">US$ {Math.round(pmt(n)).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1">
            {[["en", "🇺🇸 English"], ["pt", "🇧🇷 Português"]].map(([id, lab]) => (
              <button key={id} type="button" data-testid={`sim-lang-${id}`} onClick={() => setLang(id)}
                className={`border px-3 h-10 text-xs font-display font-bold uppercase tracking-wide transition-colors ${lang === id ? "bg-[#C9A227] border-[#C9A227] text-black" : "border-border text-text-secondary hover:border-[#C9A227]"}`}>
                {lab}
              </button>
            ))}
          </div>
          <button type="button" data-testid="sim-generate" onClick={generate} disabled={busy}
            className="bg-primary text-white hover:opacity-90 px-5 h-10 inline-flex items-center gap-2 font-display font-bold uppercase text-xs tracking-widest transition-opacity disabled:opacity-50">
            <FileText size={14} /> {busy ? "Gerando..." : "Gerar PDF"}
          </button>
        </div>
        <p className="text-[10px] text-text-secondary mt-2">A 1ª geração de cada carro demora ~15s (a IA monta a lista de equipamentos e fica salva).</p>
      </div>
    </div>,
    document.body
  );
};

export default FinanceSimModal;
